import {putAll, takeLatestF, AuthData, extractFormErrorsFromResponse} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import * as routerActions from 'app/router';
import {appRoutePaths} from 'app/routes/route-paths';
import {history} from 'app/setup/history';
import {t} from 'app/translations';
import {formHelpers} from 'utils/forms';
import {showBeError} from 'utils/show-be-error';

import {loginFormName} from '../../types/login-form-values';
import {userAction} from '../action';
import {LocationState, afterBeLoginSetup} from './saga-utils';

// Runtime type guard to validate the shape of history.location.state
const isLocationState = (obj: unknown): obj is LocationState => {
    if (!obj || typeof obj !== 'object') return false;
    const {redirectTo} = (obj as {redirectTo?: unknown});
    return redirectTo === undefined || typeof redirectTo === 'string';
};

const getLoginRouteState = (): LocationState | undefined => {
    const routeStateUnknown = history.location.state;
    return isLocationState(routeStateUnknown) ? routeStateUnknown : undefined;
};

function* execute(): SagaIterator {
    yield* put(formHelpers.startSubmit(loginFormName));

    const loginFormValues = (yield* select(formHelpers.formValues(loginFormName)))
        .orCrash('Chybí přihlašovací údaje');
    const authData: AuthData = {
        username: opt(loginFormValues.username).orCrash('missing username'),
        password: opt(loginFormValues.password).orCrash('missing password'),
    };

    const routeState = yield* select(getLoginRouteState);

    const response = yield* call(Api.login, authData);

    if (response.isSuccess) {
        // A table-settings request can finish with 401 while the user is on the
        // login screen and leave behind a partial table state. Clear it before
        // returning so tables initialize and fetch their settings again.
        yield* put(userAction.resetTablesAfterLogin());
        const redirectRoute = !routeState?.redirectTo || routeState.redirectTo === '/login'
            ? appRoutePaths.home
            : routeState.redirectTo;
        yield* put(routerActions.push(redirectRoute));
        yield* call(afterBeLoginSetup, true, authData);
        yield* put(formHelpers.reset('login'));
        yield* put(formHelpers.stopSubmit(loginFormName, {}));
    } else {
        yield putAll(showBeError(response, t('user')('login'), {isLogin: true}));
        yield* put(formHelpers.stopSubmit(loginFormName, extractFormErrorsFromResponse(response)));
    }

    yield* put(userAction.initializeFinish());
}

export function* loginSaga(): SagaIterator {
    yield takeLatestF('user/LOGIN', execute);
}
